import { DirectoryActionConsts, ItemTypeConsts } from 'client/util/Constants';
import { applicationStates } from 'client/util/Constants/ApplicationConstants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import GettingStartedPanelSettings from 'client/components/GettingStartedPanel/GettingStartedPanelSettings';

const ApplicationAsidePanel = () => import('client/applications/ApplicationAsidePanel.vue');
const ApplicationChooserAsidePanel = () => import('client/applications/ApplicationChooserAsidePanel.vue');
const ApplicationMainPanel = () => import('client/applications/ApplicationMainPanel.vue');

const {
  error,
  success,
} = applicationStates;

const applicationProps = {
  asidePanel: {
    isExpanded: true,
    itemType: ItemTypeConsts.application,
  },
  asidePanelCreate: {
    isExpanded: true,
    itemId: undefined,
    itemType: ItemTypeConsts.application,
  },
  mainPanel: {
    gettingStartedPanelItemType: ItemTypeConsts.application,
    isAsideOpen: false,
    itemType: ItemTypeConsts.application,
    selectedModelId: '',
    setSelectedModelId(selectedModelId) {
      this.selectedModelId = selectedModelId;
    },
  },
};

const ssoProps = {
  asidePanel: {
    isExpanded: true,
    itemType: ItemTypeConsts.sso,
  },
  asidePanelChooser: {
    isExpanded: true,
    itemId: undefined,
    itemType: ItemTypeConsts.sso,
  },
  mainPanel: {
    gettingStartedPanelItemType: ItemTypeConsts.sso,
    isAsideOpen: false,
    itemType: ItemTypeConsts.sso,
    selectedModelId: '',
    setSelectedModelId(selectedModelId) {
      this.selectedModelId = selectedModelId;
    },
  },
};

const metaData = {
  title: 'SSO',
  tooltipProps: {
    linkHref: GettingStartedPanelSettings[ItemTypeConsts.sso](featureFlags)
      .learnMoreUrl,
    pText: GettingStartedPanelSettings[ItemTypeConsts.sso](featureFlags).tooltipText,
  },
};

// @todo Re-evaluate if this needs to be refactored or removed once
// `2023-01-10-application-workflow` is in production
// @see https://jumpcloud.atlassian.net/browse/DE-2647
const beforeEnter = (enabledFlags) => (to, from, next) => {
  // Add a redirect for backwards compatibility of any reference to
  // `/sso/...` in the admin portal.
  const applicationWorkflow = enabledFlags[featureFlags.applicationWorkflow];

  if (applicationWorkflow) {
    const path = to.fullPath
      .replace('sso', 'applications')
      .replace('choose', 'create');
    next({
      path,
      replace: true,
    });
  } else {
    next(true);
  }
};

export default (enabledFlags) => {
  const applicationWorkflow = enabledFlags[featureFlags.applicationWorkflow];
  const routeProps = applicationWorkflow ? applicationProps : ssoProps;

  if (applicationWorkflow) metaData.title = 'Applications';

  return [
    {
      path: '/applications',
      name: 'applications',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
      },
      props: routeProps,
    },
    {
      path: '/applications/create',
      name: 'applicationCreate',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationChooserAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: routeProps.asidePanelCreate,
      },
    },
    {
      path: '/applications/configure/:id',
      name: 'applicationConfigure',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: route => ({
          ...routeProps.asidePanel,
          templateId: route.params.id,
        }),
      },
    },
    {
      path: '/applications/:id/details',
      name: 'applicationDetails',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: route => ({
          ...routeProps.asidePanel,
          itemId: route.params.id,
        }),
      },
    },
    {
      path: '/applications/:id/provision/:status',
      name: 'applicationProvisioning',
      meta: metaData,
      components: {
        mainPanel: { ...ApplicationMainPanel, isAsideOpen: true },
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: (route) => {
          const { id: itemId, status } = route.params;
          let provisionActivationStatus;

          switch (status) {
            case DirectoryActionConsts.activated:
              provisionActivationStatus = success;
              break;
            case DirectoryActionConsts.error:
              provisionActivationStatus = error;
              break;
            default:
              provisionActivationStatus = null;
          }
          return {
            ...routeProps.asidePanel,
            itemId,
            provisionActivationStatus,
          };
        },
      },
    },
    // ------------------ Cut here -------------------------------------
    // @todo Re-evaluate if these paths need to be removed once
    // `2023-01-10-application-workflow` is in production
    // @see https://jumpcloud.atlassian.net/browse/DE-2647
    {
      path: '/sso',
      name: 'sso',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
      },
      props: routeProps,
      beforeEnter: beforeEnter(enabledFlags),
    },
    {
      path: '/sso/choose',
      name: 'ssoChoose',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationChooserAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: routeProps.asidePanelChooser,
      },
      beforeEnter: beforeEnter(enabledFlags),
    },
    {
      path: '/sso/configure/:id',
      name: 'ssoConfigure',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: route => ({
          ...routeProps.asidePanel,
          templateId: route.params.id,
        }),
      },
      beforeEnter: beforeEnter(enabledFlags),
    },
    {
      path: '/sso/:id/details',
      name: 'ssoDetails',
      meta: metaData,
      components: {
        mainPanel: ApplicationMainPanel,
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: route => ({
          ...routeProps.asidePanel,
          itemId: route.params.id,
        }),
      },
      beforeEnter: beforeEnter(enabledFlags),
    },
    {
      path: '/sso/:id/provision/:status',
      name: 'ssoProvisioning',
      meta: metaData,
      components: {
        mainPanel: { ...ApplicationMainPanel, isAsideOpen: true },
        asidePanel: ApplicationAsidePanel,
      },
      props: {
        mainPanel: routeProps.mainPanel,
        asidePanel: (route) => {
          const { id: itemId, status } = route.params;
          let provisionActivationStatus;

          switch (status) {
            case DirectoryActionConsts.activated:
              provisionActivationStatus = success;
              break;
            case DirectoryActionConsts.error:
              provisionActivationStatus = error;
              break;
            default:
              provisionActivationStatus = null;
          }
          return {
            ...routeProps.asidePanel,
            itemId,
            provisionActivationStatus,
          };
        },
      },
      beforeEnter: beforeEnter(enabledFlags),
    },
    // ------------------ Cut here -------------------------------------
  ];
};
